import { Button, Tag } from 'antd';
import { Link } from 'react-router';

import type { UserPublic } from '/imports/domain';
import { fullName, isExamUser, isLTIUser } from '/imports/domain';
import { logoutLink } from '/imports/startup/client/routes';
import { ExamUserDataMatrix } from '/imports/ui/components/ExamUserDataMatrix';
import type { SubMenuType } from '/imports/ui/components/Menu';
import { UserInboxCount } from '/imports/ui/components/Messages/MessageInboxCount';
import { Icon } from '/imports/ui/foundation/Icons';
import { styled } from '/imports/ui/foundation/Theme';
import { messagesLink, userLink } from '/imports/ui/pages/user/routes';

const DEFAULT_OFFSET: [number, number] = [5, -20];

const StyledUserMenuTitle = styled('span', () => ({
  display: 'inline-block',
}));

const StyledLogoutButton = styled(Button, () => ({
  backgroundColor: 'transparent !important',
  paddingInline: 0,
  // Allow ::after pseudo-elements from useMenuPopupStyles() to escape the button's bounding box
  position: 'unset',
}));

const TagWithLeftMargin = styled(Tag, ({ tokens }) => ({
  marginLeft: tokens.margin,
}));

export const getUserMenu = (user: UserPublic): SubMenuType<'cxUserMenu'> => ({
  key: 'cxUserMenu',
  'data-testid': 'user-menu',
  style: {
    minWidth: 120,
    cursor: 'pointer',
  },
  label: (
    <StyledUserMenuTitle data-username={user.username} data-test="user-menu-title">
      <Icon name="user" />{' '}
      {(!isExamUser(user) || (isExamUser(user) && user.legi != null)) && (
        <span>{fullName(user)}</span>
      )}
      <UserInboxCount offset={!isExamUser(user) ? DEFAULT_OFFSET : [-20, -30]} />
      {isLTIUser(user) && (
        <TagWithLeftMargin color="orange" icon={<Icon name="external-link-alt" />}>
          LTI
        </TagWithLeftMargin>
      )}
      {isExamUser(user) && (
        <span style={{ display: 'inline-block', float: 'right', lineHeight: 0, marginLeft: 24 }}>
          <ExamUserDataMatrix examId={user.examId} user={user} />
        </span>
      )}
    </StyledUserMenuTitle>
  ),
  children: [
    {
      key: 'overview',
      icon: <Icon name="home" />,
      label: <Link to="/">Overview</Link>,
    },
    {
      key: 'messages',
      icon: <Icon name="inbox" />,
      label: (
        <Link to={messagesLink({})}>
          <span>Messages</span>
          <UserInboxCount offset={[5, -20]} />
        </Link>
      ),
    },
    {
      key: 'settings',
      icon: <Icon name="user-gear" />,
      label: <Link to={userLink({})}>Settings</Link>,
    },
    { type: 'divider' },
    {
      key: 'help',
      icon: <Icon name="question-circle" />,
      label: (
        <a href="https://docs.expert.ethz.ch" target="_blank" rel="noreferrer">
          Help
        </a>
      ),
    },
    !isExamUser(user)
      ? {
          key: 'logout',
          icon: <Icon name="sign-out-alt" />,
          label: (
            <form action={logoutLink({})} method="post" style={{ display: 'inline-block' }}>
              <StyledLogoutButton type="text" htmlType="submit">
                Logout
              </StyledLogoutButton>
            </form>
          ),
        }
      : null,
  ],
});
